<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, buildPageFooter } from '../functionCollection.js';
    import axios from 'axios';
    import Swal from 'sweetalert2';
    import SearchFields from './SearchFields.vue';

    const props = defineProps(['fetchUrl', 'state', 'toggleSuspendUrl', 'removeUrl']);
    const suspendedUsersList = ref(null);
    const columnsToFetch = "id,resb,nupsi_id,firstName,lastName,strasse,parkobject,reason,suspended";

    const currentPage = ref(1);
    // const maxPageNumber = Math.ceil(props.maxNumber/20);
    const maxPageNumber = ref(1);
    const usersOnPage = ref(null);
    const footerPageNumbers = ref(new Array());

    const filtersAsString = ref('');
    const receivedFilterParameters = ref([]);
    const filterParameters = [
        { index: "resb", title: "Rechnungsempfänger-Nummer", value: null },
        { index: "nupsi_id", title: "Nupsi-ID", value: null },
        { index: "firstName", title: "Vorname", value: null },
        { index: "lastName", title: "Nachname", value: null },
        { index: "strasse", title: "Straße", value: null },
        { index: "parkobject", title: "Parkobjekt", value: null },
        { index: "reason", title: "Sperrungsgrund", value: null },
        { index: "suspended", title: "Gesperrt (Ja/1 oder Nein/0)", value: null }
    ];

    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl);
        suspendedUsersList.value = await getFromAPI(url);
        maxPageNumber.value = suspendedUsersList.value.meta.last_page;

        suspendedUsersList.value = suspendedUsersList.value.data;
        usersOnPage.value = suspendedUsersList.value.length;
        footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
    })

    //if we have filters, add those to the URL
    function getFullUrl(url) {
        url = url.concat('/').concat(`?page=${currentPage.value}&per_page=25&columns=${columnsToFetch}&column=id&order=desc`);
        if (filtersAsString.value !== '') {
            url = url.concat('&filter=').concat(filtersAsString.value);
        }
        return url;
    }

    // disables page button when needed (when it's the button of the current page,
    // or the left and right buttons when we're at the start or end of the page list).
    function isButtonDisabled(origin) {
        switch (origin) {
            case 'start':
                return (currentPage.value === 1);
            case 'end':
                return currentPage.value === maxPageNumber.value;
            default:
                return (origin === currentPage.value);
        }
    }

    // calls new suspendedUsers page whenever a page change is requested (i.e. a page button is pressed in the footer)
    async function getNewPage(newPage) {
        currentPage.value = newPage;
        if (currentPage.value < 1) {
            currentPage.value = 1;
        }
        suspendedUsersList.value = await getFromAPI(getFullUrl(props.fetchUrl));
        maxPageNumber.value = suspendedUsersList.value.meta.last_page;
        suspendedUsersList.value = suspendedUsersList.value.data;
        usersOnPage.value = suspendedUsersList.value.length;
        buildPageFooter(currentPage.value, maxPageNumber.value);
    }

    //format the date to show up properly/human-readable in the list
    function formatDate(date) {
        let res = '';
        res = res.concat(date.substring(8, 10), '.', date.substring(5, 7), '.', date.substring(0, 4), ' ', date.substring(11, 19));
        return res;
    }

    // when executed, suspends or unsuspends a customer. Calls a SweetAlert2 modal.
    function toggleSuspension(id) {
        axios.put(props.toggleSuspendUrl.replace(':id', id))
        .then((data) => {
            if (data) {
                if (data.status === 200) {
                    Swal.fire({
                        title: "Erfolgreich",
                        text: data.data.message,
                        icon: 'success',
                    }).then((result) => {
                        if (result.isConfirmed === true) {
                            window.location.reload();
                        }
                    });
                } else {
                    Swal.fire({
                        title: "Fehlgeschlagen",
                        text: data.data.message,
                        icon: 'error',
                    });
                }
            }
        }).catch((error) => {
            Swal.fire({
                title: "Fehlgeschlagen",
                text: error.response.data.message,
                icon: 'error',
            });
        });
    }

    // removes a customer from the list of suspicious customers. Soft-delete.
    // Opens a SweetAlert2 modal to ask for confirmation. After confirming the successful delete,
    // refetches the current page.
    function removeUser(id) {
        Swal.fire({
            title: "Sind Sie sicher?",
            text: "Sind Sie sicher, dass Sie den Eintrag löschen wollen?",
            icon: "warning",
            confirmButtonText: "Löschen",
            confirmButtonColor: "#e64942",
            showCancelButton: true,
            cancelButtonText: "Abbrechen",
            focusCancel: true,
            reverseButtons: true,
        }).then(async (result) => {
            if (result.isConfirmed) {
                await axios.delete(props.removeUrl.replace(':id', id))
                .then((data) => {
                    if (data.status === 200) {
                        Swal.fire({
                            title: "Erfolgreich",
                            text: "Eintrag wurde als gelöscht markiert!",
                            icon: "success",
                        }).then((result) => {
                            if (result.isConfirmed === true) {
                                getNewPage(currentPage.value);
                                window.location.reload();
                            }
                        })
                    }
                })
            }
        }).catch((error) => {
            Swal.fire({
                title: "Fehlgeschlagen",
                text: error.response.data.message,
                icon: 'error',
            });
        })
    }

    // builds search query URL parameters, removing empty fields from query, and executes the search.
    function filterSearch(givenFields) {
        receivedFilterParameters.value = [];
        for (let i = 0; i < givenFields.length; i++) {
            if (givenFields[i].value !== null) {
                receivedFilterParameters.value.push(givenFields[i]);
            }
            if (givenFields[i].index === 'suspended') {
                if (givenFields[i].value === 'Ja' || givenFields[i].value === 'ja') {
                    givenFields[i].value = 1;
                } else if (givenFields[i].value === 'Nein' || givenFields[i].value === 'nein') {
                    givenFields[i].value = 0;
                }
            }
        }
        filtersAsString.value = JSON.stringify(receivedFilterParameters.value);
        getNewPage(1);
    }

</script>

<template>
    <SearchFields v-if="props.state !== 'deleted'"
        :fields="filterParameters"
        @filters="filterSearch"
    />
    <div class="main-table" v-if="suspendedUsersList && suspendedUsersList.length > 0">
        <div class="inner-table">
            <table class="table">
                <thead>
                    <tr>
                        <th class="table-head">ID</th>
                        <th class="table-head">Re.-Nr.</th>
                        <th class="table-head">Nupsi-ID</th>
                        <th class="table-head">Vorname</th>
                        <th class="table-head">Nachname</th>
                        <th class="table-head">Straße</th>
                        <th class="table-head">Parkobjekt</th>
                        <th class="table-head">Grund/Forderung</th>
                        <th class="table-head">Gesperrt</th>
                        <th class="table-head" v-if="state !== 'deleted'">IBAN</th>
                        <th class="table-head" v-if="state !== 'deleted'">Aktionen</th>
                        <th class="table-head" v-if="state === 'deleted'">Erstellt</th>
                        <th class="table-head" v-if="state === 'deleted'">Entfernt</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="m-datatable__row" v-for="suspendedUser of suspendedUsersList" :key="suspendedUser.id">
                        <td>{{ suspendedUser.id }}</td>
                        <td>{{ suspendedUser.resb }}</td>
                        <td>{{ suspendedUser.nupsi_id }}</td>
                        <td>{{ suspendedUser.firstName }}</td>
                        <td>{{ suspendedUser.lastName }}</td>
                        <td>{{ suspendedUser.strasse }}</td>
                        <td>{{ suspendedUser.parkobject }}</td>
                        <td>{{ suspendedUser.reason }}</td>
                        <td>{{ (suspendedUser.suspended === 1) ? 'Ja' : 'Nein' }}</td>
                        <td v-if="state !== 'deleted'">{{ suspendedUser.iban }}</td>
                        <td v-if="state !== 'deleted'">
                            <div class="buttons">
                                <div v-if="suspendedUser.suspended === 0">
                                    <button
                                        type="button"
                                        class="btn btn-primary"
                                        @click.prevent="toggleSuspension(suspendedUser.id)">
                                        Sperren
                                    </button>
                                </div>
                                <div v-else>
                                    <button
                                        type="button"
                                        class="btn btn-primary"
                                        @click.prevent="toggleSuspension(suspendedUser.id)">
                                        Entsperren
                                    </button>
                                </div>
                                <button type="button" class="btn btn-primary" @click.prevent="removeUser(suspendedUser.id)">
                                    <i class="fa-solid fa-trash"></i> Löschen
                                </button>
                            </div>
                        </td>
                        <td v-if="state === 'deleted'">{{ formatDate(suspendedUser.created_at) }}</td>
                        <td v-if="state === 'deleted'">{{ formatDate(suspendedUser.deleted_at) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="table-footer middle" v-if="suspendedUsersList && suspendedUsersList > 0 && footerPageNumbers.length > 1">
        <ul class="pagination">
            <li class="page-item">
                <button class="page-link" :disabled="isButtonDisabled('start')" @click="getNewPage(currentPage - 1)">
                    <i class="fas fa-lg fa-caret-left"></i>
                </button>
            </li>
            <li v-for="page in footerPageNumbers" :key="page.id" :class="{ 'active' : page == currentPage }" >
                <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page)">{{ page }}</button>
            </li>
            <li class="page-item">
                <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end')">
                    <i class="fas fa-lg fa-caret-right"></i>
                </button>
            </li>
        </ul>
    </div>

    <div class="m-3" v-if="suspendedUsersList && (suspendedUsersList.length == 0 || suspendedUsersList == null)">
        <div class="alert alert-custom" role="alert">
            <i class="fa-solid fa-comment fa-4x"></i>
            <span>Es wurden keine gesperrten Kunden gefunden.</span>
        </div>
    </div>

</template>
